import type {
  AdminAclRoleDto,
  AdminVMPaginationResponse,
  BaseErrorResponse,
  CreateAdminReq,
  EmptySuccessResponse,
  GetAclAdminRoleVMEntityRes,
  GetAdminVMEntityRes,
  UpdateAdminReq,
  SetAdminPasswordByTokenReq,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function useAdmin() {
  class Admin<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags Admin
     * @name AdminControllerDeleteEntity
     * @summary Admin delete
     * @request DELETE:/api3/admin/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    adminControllerDeleteEntity = (id: string, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/admin/${id}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Admin
     * @name AdminControllerUpdateEntity
     * @summary Admin update
     * @request PUT:/api3/admin/{id}
     * @secure
     * @response `200` `GetAdminVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    adminControllerUpdateEntity = (id: string, data: UpdateAdminReq, params: RequestParams = {}) =>
      this.request<GetAdminVMEntityRes, BaseErrorResponse>({
        path: `/api3/admin/${id}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Admin
     * @name AdminControllerGetEntityByPk
     * @summary Get Admin by PK
     * @request GET:/api3/admin/{id}
     * @secure
     * @response `200` `GetAdminVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    adminControllerGetEntityByPk = (id: string, params: RequestParams = {}) =>
      this.request<GetAdminVMEntityRes, BaseErrorResponse>({
        path: `/api3/admin/${id}`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Admin
     * @name AdminControllerCreateEntity
     * @summary Admin create
     * @request POST:/api3/admin
     * @secure
     * @response `200` `GetAdminVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    adminControllerCreateEntity = (data: CreateAdminReq, params: RequestParams = {}) =>
      this.request<GetAdminVMEntityRes, BaseErrorResponse>({
        path: '/api3/admin',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Admin
     * @name AdminControllerGetAllPaginatedEntities
     * @summary Get Admins
     * @request GET:/api3/admin
     * @secure
     * @response `200` `AdminVMPaginationResponse`
     * @response `400` `BaseErrorResponse`
     */
    adminControllerGetAllPaginatedEntities = (
      query?: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit?: number;
        /**
         * @min 0
         * @example 0
         */
        page?: number;
        /** @example [{"field":"admin.id","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"admin.email","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"admin.isEmailConfirmed","operation":"{ != | = }","value":null},{"field":"admin.firstname","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"admin.lastname","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"admin.registeredAt","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"admin.languageId","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"admin.passwordChangeAt","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"admin.phone","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"admin.isPhoneConfirmed","operation":"{ != | = }","value":null}] */
        filter?: object;
        /**
         * Sort options
         * @example {"admin.id":"{ ASC | DESC }","admin.email":"{ ASC | DESC }","admin.isEmailConfirmed":"{ ASC | DESC }","admin.firstname":"{ ASC | DESC }","admin.lastname":"{ ASC | DESC }","admin.registeredAt":"{ ASC | DESC }","admin.languageId":"{ ASC | DESC }","admin.passwordChangeAt":"{ ASC | DESC }","admin.phone":"{ ASC | DESC }","admin.isPhoneConfirmed":"{ ASC | DESC }"}
         */
        orderBy?: object;
        /** @example ["kernel_admin"] */
        roles?: string[];
      },
      params: RequestParams = {},
    ) =>
      this.request<AdminVMPaginationResponse, BaseErrorResponse>({
        path: '/api3/admin',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Admin Acl Role
     * @name AdminAclRoleControllerDeleteEntity
     * @summary Admin Acl Role delete
     * @request DELETE:/api3/admin/{adminId}/acl-role/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    adminAclRoleControllerDeleteEntity = (adminId: string, id: string, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/admin/${adminId}/acl-role/${id}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Admin Acl Role
     * @name AdminAclRoleControllerCreateEntity
     * @summary Admin Acl Role create
     * @request POST:/api3/admin/{adminId}/acl-role
     * @secure
     * @response `200` `GetAclAdminRoleVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    adminAclRoleControllerCreateEntity = (adminId: string, data: AdminAclRoleDto, params: RequestParams = {}) =>
      this.request<GetAclAdminRoleVMEntityRes, BaseErrorResponse>({
        path: `/api3/admin/${adminId}/acl-role`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Admin
     * @name AdminInviteControllerInvite
     * @summary Send admin invite email
     * @request POST:/api3/admin/{id}/invite
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    adminInviteControllerInvite = (id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/admin/${id}/invite`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Admin
     * @name AdminInviteControllerSetPasswordByToken
     * @summary Set admin password by invite token
     * @request POST:/api3/admin/invite/set-password
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    adminInviteControllerSetPasswordByToken = (data: SetAdminPasswordByTokenReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: '/api3/admin/invite/set-password',
        method: 'POST',
        body: data,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });
  }

  return new Admin();
}
